import Papa from "papaparse";
import React, { useCallback, useEffect, useState } from "react";
import { Badge, Col, Row, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstTable from "../../components/DstTable/DstTable";
import MultiSelectDropdown from "../../components/MultiSelectDropdown/MultiSelectDropdown";
import { useSession } from "../../contexts/SessionContext";
import { normalizeDate } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate, detectCsvDelimiter } from "../../services/UtilityService";
import { MeetingImportFile, MeetingImportStructuredData } from "./MeetingImport.d";
import {
  MEETING_IMPORT_LANGUAGES,
  createImportedMeetings,
  fetchAnimators,
  fetchCohorts,
  fetchPrograms,
  fetchProjects,
  getMeetingImportLanguageLabel,
} from "./MeetingImport.function";
import "./MeetingImport.scss";

export const MeetingImport = () => {
  const { language, showAlert, popAlert } = useSession();
  const [csvData, setCsvData] = useState<Array<MeetingImportStructuredData>>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; columnKey: string } | null>(null);
  const [animators, setAnimators] = useState<any[]>([]);
  const [cohorts, setCohorts] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchAnimators(setAnimators, language),
          fetchCohorts(setCohorts, language),
          fetchPrograms(setPrograms, language),
          fetchProjects(setProjects, language),
        ]);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [language]);

  const onFileUpload = useCallback(
    (file: File) => {
      detectCsvDelimiter(file).then((delimiter) => {
        Papa.parse<MeetingImportFile>(file, {
          header: true,
          skipEmptyLines: true,
          delimiter: delimiter,
          complete: (results: Papa.ParseResult<MeetingImportFile>) => {
            if (results.errors.length > 0) {
              console.error("CSV Parse Errors:", results.errors);
              alert(translate(language, "PAGES.MEETING_IMPORT.ERROR_CSV_IMPORT"));
              return;
            }

            const transformedData = results.data
              .map((row: MeetingImportFile) => ({
                cohort_id: row.cohort_id,
                project_id: row.project_id_opt,
                meeting_type_id: row.meeting_type_id,
                meeting_id: row.meeting_id_opt,
                name: row.meeting_name,
                custom_meeting_link: row.custom_meeting_link_opt,
                service_link_generation: row.service_link_generation_opt,
                date: normalizeDate(row.date),
                start: row.start,
                organizer: row.organizer_id,
                visibility: row.visibility?.toLowerCase() === "datascientest" ? "shared" : row.visibility,
                animator: row.animator_id_opt,
                duration: row.duration_min,
                note: row.note_opt,
                language: row.language_opt,
                program_id: row.program_id_opt,
              }))
              .map((row, index) => ({
                ...row,
                _index: index,
              }));

            setCsvData(transformedData);
          },
          error: (error: Error) => {
            console.error("Error parsing CSV:", error);
            alert(`${translate(language, "PAGES.MEETING_IMPORT.ERROR_CSV_IMPORT")} ${error.message}`);
          },
        });
      });
    },
    [language]
  );

  const onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onFileUpload(file);
    }
  };

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const file = event.dataTransfer?.files[0];
      if (file) {
        onFileUpload(file);
      }
    },
    [onFileUpload]
  );

  const onCellChange = (rowIndex: number, columnKey: string, value: string) => {
    const newData = [...csvData];
    newData[rowIndex] = {
      ...newData[rowIndex],
      [columnKey]: value,
    };
    setCsvData(newData);
    setEditingCell(null);
  };

  const columns = [
    {
      name: translate(language, "PAGES.MEETING_IMPORT.COHORT_ID"),
      key: "cohort_id",
      render: (line: MeetingImportStructuredData) => (
        <div className="meeting-import-cell d-flex align-items-center justify-content-center">
          <MultiSelectDropdown
            label={
              <div className="d-flex align-items-center justify-content-center gap-2 text-center">
                <div className="text-wrap text-break text-center">
                  {line.cohort_id?.split(",").map((cohort_id: string) => (
                    <Badge key={cohort_id} className="me-2 xxs-regular">
                      {cohort_id.trim()}
                    </Badge>
                  ))}
                </div>
                <span className="dst-icon-edit-02 clickable dropdown-icon" />
              </div>
            }
            displayItem={(item) => item.id}
            badgeItemProperty="id"
            items={cohorts}
            defaultSelectedItems={cohorts.filter((cohort) =>
              line.cohort_id
                ?.split(",")
                .map((id) => Number(id))
                .includes(cohort.id)
            )}
            onConfirm={(selectedItems: any[]) => {
              const newData = [...csvData];
              newData[line._index ?? 0] = {
                ...newData[line._index ?? 0],
                cohort_id: selectedItems.map((item) => item.id).join(","),
              };
              setCsvData(newData);
            }}
            language={language}
            showAlwaysConfirm
            confirmOnClose
            hideCaret
            toggleClass="d-flex align-items-center justify-content-center w-100"
          />
        </div>
      ),
    },
    {
      name: translate(language, "PAGES.MEETING_IMPORT.PROJECT_ID"),
      key: "project_id",
      render: (line: MeetingImportStructuredData) => (
        <div className="meeting-import-cell d-flex align-items-center justify-content-center">
          <MultiSelectDropdown
            label={
              <div className="d-flex align-items-center justify-content-center gap-2 text-center">
                <Badge className="me-2 xxs-regular">{line.project_id}</Badge>
                <span className="dst-icon-edit-02 clickable dropdown-icon" />
              </div>
            }
            displayItem={(item) => item.id}
            badgeItemProperty="id"
            items={projects}
            defaultSelectedItems={projects.filter((project) => project.id === Number(line.project_id))}
            onConfirm={(selectedItems: any[]) => {
              const newData = [...csvData];
              newData[line._index ?? 0] = {
                ...newData[line._index ?? 0],
                project_id: selectedItems[0]?.id,
              };
              setCsvData(newData);
            }}
            confirmOnClose
            language={language}
            showAlwaysConfirm
            hideCaret
            toggleClass="d-flex align-items-center justify-content-center w-100"
            isSingleSelect
            isSingleSelectRemovable
          />
        </div>
      ),
    },
    {
      name: translate(language, "PAGES.MEETING_IMPORT.MEETING_TYPE_ID"),
      key: "meeting_type_id",
      render: (line: MeetingImportStructuredData) =>
        editingCell?.rowIndex === line._index && editingCell?.columnKey === "meeting_type_id" ? (
          <div className="meeting-import-cell">
            <input
              type="text"
              defaultValue={line.meeting_type_id}
              onBlur={(event) => onCellChange(line._index ?? 0, "meeting_type_id", event.target.value)}
            />
          </div>
        ) : (
          <div
            className="meeting-import-cell d-flex align-items-center justify-content-center gap-2 text-center clickable"
            onClick={() => setEditingCell({ rowIndex: line._index ?? 0, columnKey: "meeting_type_id" })}
          >
            {line.meeting_type_id}
            <span className="dst-icon-edit-02 clickable dropdown-icon" />
          </div>
        ),
    },
    {
      name: translate(language, "PAGES.MEETING_IMPORT.PROGRAM"),
      key: "program_id",
      render: (line: MeetingImportStructuredData) => (
        <div className="meeting-import-cell d-flex align-items-center justify-content-center">
          <MultiSelectDropdown
            label={
              <div className="d-flex align-items-center justify-content-center gap-2 text-center">
                <Badge className="me-2 xxs-regular">
                  {programs.find((program) => program.id === Number(line.program_id))?.name ?? line.program_id ?? "—"}
                </Badge>
                <span className="dst-icon-edit-02 clickable dropdown-icon" />
              </div>
            }
            displayItem={(item) => item.name}
            badgeItemProperty="id"
            items={programs}
            defaultSelectedItems={programs.filter((program) => program.id === Number(line.program_id))}
            onConfirm={(selectedItems: any[]) => {
              const newData = [...csvData];
              newData[line._index ?? 0] = {
                ...newData[line._index ?? 0],
                program_id: selectedItems[0]?.id,
              };
              setCsvData(newData);
            }}
            language={language}
            hideCaret
            toggleClass="d-flex align-items-center justify-content-center w-100"
            isSingleSelect
            isSingleSelectRemovable
          />
        </div>
      ),
    },
    {
      name: translate(language, "GLOBAL.LANGUAGE"),
      key: "language",
      render: (line: MeetingImportStructuredData) => (
        <div className="meeting-import-cell d-flex align-items-center justify-content-center">
          <MultiSelectDropdown
            label={
              <div className="d-flex align-items-center justify-content-center gap-2 text-center">
                <Badge className="me-2 xxs-regular">{getMeetingImportLanguageLabel(line.language, language)}</Badge>
                <span className="dst-icon-edit-02 clickable dropdown-icon" />
              </div>
            }
            displayItem={(item) => item.label}
            badgeItemProperty="value"
            items={MEETING_IMPORT_LANGUAGES.map(({ locale, translationKey }) => ({
              id: locale,
              value: locale,
              label: translate(language, translationKey),
            }))}
            defaultSelectedItems={(() => {
              const found = MEETING_IMPORT_LANGUAGES.find(({ locale }) => locale === line.language);
              return found
                ? [{ id: found.locale, value: found.locale, label: translate(language, found.translationKey) }]
                : undefined;
            })()}
            onConfirm={(selectedItems: any[]) => {
              const newData = [...csvData];
              newData[line._index ?? 0] = {
                ...newData[line._index ?? 0],
                language: selectedItems[0]?.value,
              };
              setCsvData(newData);
            }}
            language={language}
            hideCaret
            toggleClass="d-flex align-items-center justify-content-center w-100"
            isSingleSelect
            isSingleSelectRemovable
          />
        </div>
      ),
    },
    {
      name: translate(language, "PAGES.MEETING_IMPORT.MEETING_ID"),
      key: "meeting_id",
      render: (line: MeetingImportStructuredData) =>
        editingCell?.rowIndex === line._index && editingCell?.columnKey === "meeting_id" ? (
          <div className="meeting-import-cell">
            <input
              type="text"
              defaultValue={line.meeting_id}
              onBlur={(event) => onCellChange(line._index ?? 0, "meeting_id", event.target.value)}
            />
          </div>
        ) : (
          <div
            className="meeting-import-cell d-flex align-items-center justify-content-center gap-2 text-center clickable"
            onClick={() => setEditingCell({ rowIndex: line._index ?? 0, columnKey: "meeting_id" })}
          >
            {line.meeting_id}
            <span className="dst-icon-edit-02 clickable dropdown-icon" />
          </div>
        ),
    },
    {
      name: translate(language, "GLOBAL.NAME"),
      key: "name",
      render: (line: MeetingImportStructuredData) =>
        editingCell?.rowIndex === line._index && editingCell?.columnKey === "name" ? (
          <div className="meeting-import-cell">
            <input
              type="text"
              defaultValue={line.name}
              onBlur={(event) => onCellChange(line._index ?? 0, "name", event.target.value)}
            />
          </div>
        ) : (
          <div
            className="meeting-import-cell d-flex align-items-center justify-content-center gap-2 text-center clickable"
            onClick={() => setEditingCell({ rowIndex: line._index ?? 0, columnKey: "name" })}
          >
            {line.name}
            <span className="dst-icon-edit-02 clickable dropdown-icon" />
          </div>
        ),
    },
    {
      name: translate(language, "PAGES.MEETING_IMPORT.CUSTOM_MEETING_LINK"),
      key: "custom_meeting_link",
      render: (line: MeetingImportStructuredData) =>
        editingCell?.rowIndex === line._index && editingCell?.columnKey === "custom_meeting_link" ? (
          <div className="meeting-import-cell text-center">
            <input
              type="text"
              defaultValue={line.custom_meeting_link}
              onBlur={(event) => onCellChange(line._index ?? 0, "custom_meeting_link", event.target.value)}
            />
          </div>
        ) : (
          <div
            className="meeting-import-cell d-flex align-items-center justify-content-center gap-2 text-center clickable"
            onClick={() => setEditingCell({ rowIndex: line._index ?? 0, columnKey: "custom_meeting_link" })}
          >
            <div className="text-break">
              {line.custom_meeting_link ||
                translate(
                  language,
                  line.service_link_generation === "ZOOM"
                    ? "PAGES.MEETING_IMPORT.AUTO_ZOOM_LINK"
                    : line.service_link_generation === "TEAMS"
                    ? "PAGES.MEETING_IMPORT.AUTO_TEAMS_LINK"
                    : "PAGES.MEETING_IMPORT.NO_MEETING_SERVICE_LINK_GENERATION"
                )}
            </div>
            <span className="dst-icon-edit-02 clickable dropdown-icon" />
          </div>
        ),
    },
    {
      name: translate(language, "GLOBAL.DATE"),
      key: "date",
      render: (line: MeetingImportStructuredData) =>
        editingCell?.rowIndex === line._index && editingCell?.columnKey === "date" ? (
          <input
            type="date"
            className="meeting-import-cell"
            defaultValue={line.date}
            onBlur={(event) => onCellChange(line._index ?? 0, "date", event.target.value)}
          />
        ) : (
          <div
            className="meeting-import-cell d-flex align-items-center justify-content-center gap-2 text-center clickable"
            onClick={() => setEditingCell({ rowIndex: line._index ?? 0, columnKey: "date" })}
          >
            {line.date}
            <span className="dst-icon-calendar-date clickable dropdown-icon" />
          </div>
        ),
    },
    {
      name: translate(language, "PAGES.MEETING_IMPORT.START"),
      key: "start",
      render: (line: MeetingImportStructuredData) =>
        editingCell?.rowIndex === line._index && editingCell?.columnKey === "start" ? (
          <input
            type="time"
            defaultValue={line.start}
            className="meeting-import-cell"
            onBlur={(event) => onCellChange(line._index ?? 0, "start", event.target.value)}
          />
        ) : (
          <div
            className="meeting-import-cell d-flex align-items-center justify-content-center gap-2 text-center clickable"
            onClick={() => setEditingCell({ rowIndex: line._index ?? 0, columnKey: "start" })}
          >
            {line.start}
            <span className="dst-icon-alarm-clock clickable dropdown-icon" />
          </div>
        ),
    },
    {
      name: translate(language, "PAGES.MEETING_IMPORT.DURATION"),
      key: "duration",
      render: (line: MeetingImportStructuredData) =>
        editingCell?.rowIndex === line._index && editingCell?.columnKey === "duration" ? (
          <input
            type="number"
            defaultValue={line.duration}
            onBlur={(event) => onCellChange(line._index ?? 0, "duration", event.target.value)}
            className="meeting-import-cell"
          />
        ) : (
          <div
            className="meeting-import-cell d-flex align-items-center justify-content-center gap-2  text-center clickable"
            onClick={() => setEditingCell({ rowIndex: line._index ?? 0, columnKey: "duration" })}
          >
            {line.duration}
            <span className="dst-icon-alarm-clock clickable dropdown-icon" />
          </div>
        ),
    },
    {
      name: translate(language, "GLOBAL.ORGANIZER"),
      key: "organizer",
    },
    {
      name: translate(language, "PAGES.MEETING_IMPORT.VISIBILITY"),
      key: "visibility",
      render: (line: MeetingImportStructuredData) => (
        <div className="meeting-import-cell d-flex align-items-center justify-content-center">
          <MultiSelectDropdown
            label={
              <div className="d-flex align-items-center justify-content-center gap-2 text-center">
                <Badge className="me-2 xxs-regular">{line.visibility}</Badge>
                <span className="dst-icon-eye clickable dropdown-icon" />
              </div>
            }
            displayItem={(item) => item.label}
            badgeItemProperty="value"
            items={[
              { id: 1, value: "private", label: translate(language, "PAGES.MEETING_IMPORT.PRIVATE") },
              { id: 2, value: "shared", label: translate(language, "PAGES.MEETING_IMPORT.SHARED") },
            ]}
            onConfirm={(selectedItems: any[]) => {
              const newData = [...csvData];
              newData[line._index ?? 0] = {
                ...newData[line._index ?? 0],
                visibility: selectedItems[0]?.value,
              };
              setCsvData(newData);
            }}
            language={language}
            showAlwaysConfirm
            confirmOnClose
            hideCaret
            isSingleSelect
          />
        </div>
      ),
    },
    {
      name: translate(language, "PAGES.MEETING_IMPORT.ANIMATORS"),
      key: "animator",
      render: (line: MeetingImportStructuredData) => (
        <div className="meeting-import-cell d-flex align-items-center justify-content-center w-100 text-center">
          <MultiSelectDropdown
            label={
              <div className="d-flex align-items-center justify-content-center gap-2 text-center">
                <div className="text-wrap text-break text-center">
                  {line.animator?.split(",").map((animator: string) => (
                    <Badge key={animator} className="me-2 xxs-regular">
                      {animator.trim()}
                    </Badge>
                  ))}
                </div>
                <span className="dst-icon-user-edit flex-shrink-0 dropdown-icon" />
              </div>
            }
            displayItem={(item) => item.full_name}
            badgeItemProperty="id"
            items={animators}
            defaultSelectedItems={animators.filter((animator) =>
              line.animator
                ?.split(",")
                .map((username) => username.trim())
                .includes(animator.email)
            )}
            onConfirm={(selectedItems: any[]) => {
              const newData = [...csvData];
              newData[line._index ?? 0] = {
                ...newData[line._index ?? 0],
                animator: selectedItems.map((item) => item.email).join(","),
              };
              setCsvData(newData);
            }}
            language={language}
            showAlwaysConfirm
            confirmOnClose
            hideCaret
            toggleClass="d-flex align-items-center justify-content-center w-100"
          />
        </div>
      ),
    },
    {
      name: translate(language, "PAGES.MEETING_IMPORT.NOTE"),
      key: "note",
      render: (line: MeetingImportStructuredData) =>
        editingCell?.rowIndex === line._index && editingCell?.columnKey === "note" ? (
          <div className="meeting-import-cell">
            <textarea
              rows={3}
              defaultValue={line.note}
              onBlur={(event) => onCellChange(line._index ?? 0, "note", event.target.value)}
              className="form-control"
            />
          </div>
        ) : (
          <div
            className="meeting-import-cell d-flex align-items-center justify-content-center gap-2 text-center clickable"
            onClick={() => setEditingCell({ rowIndex: line._index ?? 0, columnKey: "note" })}
          >
            {line.note}
            <span className="dst-icon-edit-02 clickable dropdown-icon" />
          </div>
        ),
    },
  ];

  if (loading) {
    return (
      <div className="p-3 w-100 d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
        <Spinner animation="border" role="status"></Spinner>
      </div>
    );
  }

  return (
    <div className="p-3 w-100" id="meeting-import">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex ">
              <span
                className="dst-icon-chevron-left p-2 medium-gray-background rounded me-2"
                onClick={(event: React.MouseEvent<HTMLDivElement>) => completeNavigate(event, navigate, "/meetings")}
              />
              <h2 className="mb-0">{translate(language, "PAGES.MEETING_IMPORT.TITLE")}</h2>
            </div>
            <div className="d-flex gap-2">
              {csvData.length > 0 && (
                <DstButton
                  btnImageBefore={<span className="dst-icon-upload-03 me-2" />}
                  value={translate(language, "PAGES.MEETING_IMPORT.NEW_CSV_IMPORT")}
                  variant="light"
                  clickFunction={() => {
                    setCsvData([]);
                    setEditingCell(null);
                    const input = document.getElementById("fileInput") as HTMLInputElement | null;
                    if (input) {
                      input.value = "";
                      input.click();
                    }
                  }}
                />
              )}
              <DstButton
                btnImageBefore={<span className="dst-icon-download-02 me-2" />}
                value={translate(language, "PAGES.MEETING_IMPORT.DOWNLOAD_TEMPLATE")}
                variant="light"
                clickFunction={() => {
                  const link = document.createElement("a");
                  link.href = "/share/meeting_template.csv";
                  link.download = "import_meeting_template.csv";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              />
              <DstButton
                btnImageAfter={<span className="dst-icon-arrow-right ms-2" />}
                value={translate(language, "PAGES.MEETING_IMPORT.CREATE_MEETINGS")}
                variant="primary"
                loading={submitting}
                disabled={submitting || csvData.length === 0}
                clickFunction={() => {
                  if (showAlert && popAlert) {
                    createImportedMeetings(
                      csvData.map(({ _index, ...rest }) => rest),
                      language,
                      setSubmitting,
                      showAlert,
                      popAlert,
                      setCsvData
                    );
                  }
                }}
              />
            </div>
          </div>
        </Col>
      </Row>
      {csvData.length === 0 ? (
        <div className="d-flex flex-column gap-2 clickable">
          <DstTable data={csvData} columns={columns} itemsPerPage={20} />
          <div
            className="border-2 border-dashed rounded-3 p-5 text-center d-flex flex-column align-items-center"
            onDragOver={(event) => event.preventDefault()}
            onDrop={onDrop}
          >
            <div className="text-center s-regular d-flex align-items-center">
              {translate(language, "PAGES.MEETING_IMPORT.DROP_FILE")}
              <DstButton
                value={translate(language, "PAGES.MEETING_IMPORT.LOAD_LOCAL_FILE")}
                variant="link"
                btnClass="ps-1"
                clickFunction={() => document.getElementById("fileInput")?.click()}
              />
            </div>
          </div>
        </div>
      ) : (
        <React.Fragment>
          <DstTable
            data={csvData.map((item, index) => ({ ...item, _index: index }))}
            columns={columns}
            itemsPerPage={20}
            classTable="xs-bold vertical-align-middle"
          />
        </React.Fragment>
      )}
      <input type="file" id="fileInput" className="d-none" onChange={onFileInputChange} />
    </div>
  );
};

export default MeetingImport;
